/**
 * Per-user favorites — visual bookmarks for venues, series, and events.
 *
 * - Loads `/api/favorites` once on sign-in and caches in three `Set<string>`.
 * - Exposes `useFavorites()` for components to check and toggle.
 * - Optimistic mutations: local state flips immediately, network call follows;
 *   on failure the toggle is reverted.
 * - Mirrors the SWR-style localStorage pattern from `lib/swrCache.ts` so the
 *   star highlights render instantly on cold reload before the network call
 *   completes.
 *
 * Distinct from `notifications-api.ts`: favoriting does NOT create a
 * Subscription and never sends SMS / email / calendar invites. It is a
 * pure UI signal so users can pick out venues / series / events they care
 * about visually.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { api } from './api';
import { isAuthPending } from './api';
import type { ExtractedEvent, FavoriteKind, FavoritesPayload } from '../types';
import { toast } from './dialogs';

// One-time hint shown the first time a user saves an event, pointing them at the
// day-before saved-event reminder setting on the Notifications page.
const SAVED_HINT_KEY = 'mh.savedReminderHintShown.v1';

function maybeShowSavedReminderHint() {
  try {
    if (localStorage.getItem(SAVED_HINT_KEY)) return;
    localStorage.setItem(SAVED_HINT_KEY, '1');
    toast.info(
      "Saved! We'll text or email you the day before your saved events. Manage this on the Notifications page.",
      { durationMs: 9000 }
    );
  } catch {
    // localStorage unavailable (private mode) — skip the hint, never block the save.
  }
}

// Bumped v2 → v3 when `dislikeIds` was added. Older cached payloads are
// missing `dislikeEventIds`, so we discard them on load.
const STORAGE_KEY = 'mh.favorites.v3';

interface FavoritesState {
  venueIds: ReadonlySet<string>;
  seriesIds: ReadonlySet<string>;
  eventIds: ReadonlySet<string>;
  tasteIds: ReadonlySet<string>;
  dislikeIds: ReadonlySet<string>;
}

/**
 * Combined per-event reaction. Replaces the old separate star (favorite) + 👍
 * (taste) + 👎 (dislike) controls with a single two-state model:
 *  - `save`          → bookmark the event (drives the Saved/Favorites filter)
 *                      AND record a positive signal that trains the "For You"
 *                      recommender (writes both `eventIds` + `tasteIds`).
 *  - `notInterested` → negative recommender signal (writes `dislikeIds`).
 *  - `none`          → clear the reaction entirely.
 * `save` and `notInterested` are mutually exclusive.
 */
export type EventReaction = 'save' | 'notInterested' | 'none';

interface FavoritesContextValue extends FavoritesState {
  /** True once we've completed the initial server fetch (or restored from cache). */
  ready: boolean;
  isFavorite: (kind: FavoriteKind, id: string) => boolean;
  toggle: (kind: FavoriteKind, id: string) => Promise<void>;
  /** True when the event is saved (bookmarked + positive taste signal). */
  isSaved: (eventId: string) => boolean;
  /** True when the event is marked "Not interested". */
  isNotInterested: (eventId: string) => boolean;
  /** Apply a combined event reaction. See {@link EventReaction}. */
  setEventReaction: (eventId: string, reaction: EventReaction) => Promise<void>;
  refresh: () => Promise<void>;
}

const EMPTY_STATE: FavoritesState = {
  venueIds: new Set<string>(),
  seriesIds: new Set<string>(),
  eventIds: new Set<string>(),
  tasteIds: new Set<string>(),
  dislikeIds: new Set<string>()
};

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

function payloadToState(p: FavoritesPayload): FavoritesState {
  return {
    venueIds: new Set(p.venueIds),
    seriesIds: new Set(p.seriesIds),
    eventIds: new Set(p.eventIds),
    // Defensive: server may be on an older deploy without these fields yet.
    tasteIds: new Set(p.tasteEventIds ?? []),
    dislikeIds: new Set(p.dislikeEventIds ?? [])
  };
}

function stateToPayload(s: FavoritesState): FavoritesPayload {
  return {
    venueIds: Array.from(s.venueIds),
    seriesIds: Array.from(s.seriesIds),
    eventIds: Array.from(s.eventIds),
    tasteEventIds: Array.from(s.tasteIds),
    dislikeEventIds: Array.from(s.dislikeIds)
  };
}

function readCachedPayload(): FavoritesPayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FavoritesPayload;
    if (!parsed || !Array.isArray(parsed.venueIds)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCachedPayload(payload: FavoritesPayload): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota exceeded etc — non-fatal */
  }
}

function clearCachedPayload(): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* */ }
}

function withId(set: ReadonlySet<string>, id: string, present: boolean): ReadonlySet<string> {
  // Always create a new Set so React notices the reference change.
  const next = new Set(set);
  if (present) next.add(id);
  else next.delete(id);
  return next;
}

function setForKind(state: FavoritesState, kind: FavoriteKind): ReadonlySet<string> {
  switch (kind) {
    case 'venue':   return state.venueIds;
    case 'series':  return state.seriesIds;
    case 'event':   return state.eventIds;
    case 'taste':   return state.tasteIds;
    case 'dislike': return state.dislikeIds;
  }
}

function applyKind(state: FavoritesState, kind: FavoriteKind, id: string, present: boolean): FavoritesState {
  switch (kind) {
    case 'venue':   return { ...state, venueIds:   withId(state.venueIds,   id, present) };
    case 'series':  return { ...state, seriesIds:  withId(state.seriesIds,  id, present) };
    case 'event':   return { ...state, eventIds:   withId(state.eventIds,   id, present) };
    case 'taste':   return { ...state, tasteIds:   withId(state.tasteIds,   id, present) };
    case 'dislike': return { ...state, dislikeIds: withId(state.dislikeIds, id, present) };
  }
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const isAuthed = useIsAuthenticated();
  const [state, setState] = useState<FavoritesState>(EMPTY_STATE);
  const [ready, setReady] = useState(false);
  // Track in-flight toggles by `${kind}:${id}` so rapid double-clicks don't
  // race the network. We just no-op the second click; the user can re-click
  // once the first round-trip resolves.
  const inflight = useRef<Set<string>>(new Set());

  // Hydrate from localStorage immediately so the star highlights render on
  // cold reload before the API fetch completes (SWR-style).
  useEffect(() => {
    if (!isAuthed) {
      setState(EMPTY_STATE);
      setReady(false);
      clearCachedPayload();
      return;
    }
    const cached = readCachedPayload();
    if (cached) {
      setState(payloadToState(cached));
      setReady(true);
    }
    let cancelled = false;
    api.get<FavoritesPayload>('/api/favorites')
      .then((payload) => {
        if (cancelled) return;
        const next = payloadToState(payload);
        setState(next);
        setReady(true);
        writeCachedPayload(payload);
      })
      .catch((err) => {
        if (isAuthPending(err)) return;
        // Non-fatal — we keep whatever cached state we had.
        // eslint-disable-next-line no-console
        console.warn('Could not load favorites:', err);
        setReady(true);
      });
    return () => { cancelled = true; };
  }, [isAuthed]);

  const isFavorite = useCallback(
    (kind: FavoriteKind, id: string) => setForKind(state, kind).has(id),
    [state]
  );

  const refresh = useCallback(async () => {
    if (!isAuthed) return;
    const payload = await api.get<FavoritesPayload>('/api/favorites');
    setState(payloadToState(payload));
    writeCachedPayload(payload);
    setReady(true);
  }, [isAuthed]);

  const toggle = useCallback(async (kind: FavoriteKind, id: string) => {
    if (!isAuthed) return;
    if (!id) return;
    const key = `${kind}:${id}`;
    if (inflight.current.has(key)) return;
    inflight.current.add(key);
    const previouslyFav = setForKind(state, kind).has(id);
    const nextFav = !previouslyFav;
    // Mutual exclusivity: taste ⇔ dislike. Activating one clears the other so
    // the recommender never sees an event marked as both "want" and "not
    // interested". `oppositeKind` is null for all other kinds.
    const oppositeKind: FavoriteKind | null = nextFav && kind === 'taste'
      ? 'dislike'
      : nextFav && kind === 'dislike'
        ? 'taste'
        : null;
    const previouslyOpposite = oppositeKind
      ? setForKind(state, oppositeKind).has(id)
      : false;
    // Optimistic local update — flip the active kind and (if applicable)
    // clear the opposite kind in a single state transition.
    let optimistic = applyKind(state, kind, id, nextFav);
    if (oppositeKind && previouslyOpposite) {
      optimistic = applyKind(optimistic, oppositeKind, id, false);
    }
    setState(optimistic);
    writeCachedPayload(stateToPayload(optimistic));
    try {
      const path = `/api/favorites/${kind}/${encodeURIComponent(id)}`;
      if (nextFav) await api.put<void>(path);
      else await api.del<void>(path);
      // Fire-and-forget the opposite-kind clear; failure here only affects
      // server state on the next reload, not the optimistic local view.
      if (oppositeKind && previouslyOpposite) {
        const oppPath = `/api/favorites/${oppositeKind}/${encodeURIComponent(id)}`;
        api.del<void>(oppPath).catch((err) => {
          if (!isAuthPending(err)) {
            // eslint-disable-next-line no-console
            console.warn(`Could not clear opposite ${oppositeKind} favorite:`, err);
          }
        });
      }
    } catch (err) {
      if (!isAuthPending(err)) {
        // Revert on failure of the primary toggle. Leave the opposite-kind
        // clear alone — it's fire-and-forget and idempotent.
        let reverted = applyKind(optimistic, kind, id, previouslyFav);
        if (oppositeKind && previouslyOpposite) {
          reverted = applyKind(reverted, oppositeKind, id, true);
        }
        setState(reverted);
        writeCachedPayload(stateToPayload(reverted));
        // eslint-disable-next-line no-console
        console.warn(`Could not ${nextFav ? 'add' : 'remove'} favorite:`, err);
      }
    } finally {
      inflight.current.delete(key);
    }
  }, [isAuthed, state]);

  const isSaved = useCallback(
    (eventId: string) => state.eventIds.has(eventId),
    [state]
  );

  const isNotInterested = useCallback(
    (eventId: string) => state.dislikeIds.has(eventId),
    [state]
  );

  const setEventReaction = useCallback(async (eventId: string, reaction: EventReaction) => {
    if (!isAuthed) return;
    if (!eventId) return;
    const key = `reaction:${eventId}`;
    if (inflight.current.has(key)) return;
    inflight.current.add(key);

    const wasSaved = state.eventIds.has(eventId);
    const wasTaste = state.tasteIds.has(eventId);
    const wasDislike = state.dislikeIds.has(eventId);

    // `save` writes BOTH the bookmark (eventIds) and the positive recommender
    // signal (tasteIds); `notInterested` writes the negative signal. The two
    // are mutually exclusive, so activating one clears the other.
    const nextSaved = reaction === 'save';
    const nextTaste = reaction === 'save';
    const nextDislike = reaction === 'notInterested';

    // Optimistic update — flip all three stores in a single state transition so
    // rapid toggles can't race a stale `state` closure (see `toggle`).
    let optimistic = applyKind(state, 'event', eventId, nextSaved);
    optimistic = applyKind(optimistic, 'taste', eventId, nextTaste);
    optimistic = applyKind(optimistic, 'dislike', eventId, nextDislike);
    setState(optimistic);
    writeCachedPayload(stateToPayload(optimistic));

    // First genuine save → surface the saved-event reminder feature once.
    if (nextSaved && !wasSaved) maybeShowSavedReminderHint();

    try {
      await api.put<void>(`/api/favorites/events/${encodeURIComponent(eventId)}/reaction`, { reaction });
    } catch (err) {
      if (!isAuthPending(err)) {
        // Revert every store back to the pre-click snapshot.
        let reverted = applyKind(optimistic, 'event', eventId, wasSaved);
        reverted = applyKind(reverted, 'taste', eventId, wasTaste);
        reverted = applyKind(reverted, 'dislike', eventId, wasDislike);
        setState(reverted);
        writeCachedPayload(stateToPayload(reverted));
        // eslint-disable-next-line no-console
        console.warn('Could not update event reaction:', err);
        toast.error('Could not update your reaction. Please try again.');
      }
    } finally {
      inflight.current.delete(key);
    }
  }, [isAuthed, state]);

  const value = useMemo<FavoritesContextValue>(() => ({
    venueIds: state.venueIds,
    seriesIds: state.seriesIds,
    eventIds: state.eventIds,
    tasteIds: state.tasteIds,
    dislikeIds: state.dislikeIds,
    ready,
    isFavorite,
    toggle,
    isSaved,
    isNotInterested,
    setEventReaction,
    refresh
  }), [state, ready, isFavorite, toggle, isSaved, isNotInterested, setEventReaction, refresh]);

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites(): FavoritesContextValue {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    // Permissive fallback — components rendered outside the provider (e.g.
    // unit-test snapshots) get an inert "no favorites" view instead of a
    // hard crash. The provider is mounted at the root in `main.tsx` so this
    // shouldn't happen in production.
    return {
      ...EMPTY_STATE,
      ready: false,
      isFavorite: () => false,
      toggle: async () => { /* no-op */ },
      isSaved: () => false,
      isNotInterested: () => false,
      setEventReaction: async () => { /* no-op */ },
      refresh: async () => { /* no-op */ }
    };
  }
  return ctx;
}

/**
 * Returns `true` when the event itself is favorited OR the venue/series it
 * resolves to is favorited (inherited highlight).
 */
export function isEventHighlighted(
  event: Pick<ExtractedEvent, 'rowKey' | 'venueId' | 'seriesId'>,
  fav: Pick<FavoritesContextValue, 'venueIds' | 'seriesIds' | 'eventIds'>
): boolean {
  if (fav.eventIds.has(event.rowKey)) return true;
  if (event.venueId && fav.venueIds.has(event.venueId)) return true;
  if (event.seriesId && fav.seriesIds.has(event.seriesId)) return true;
  return false;
}

/**
 * Tells UI WHY an event is highlighted — used for tooltip / aria text so
 * users can understand the inherited highlight ("Favorited via venue: …").
 */
export function eventFavoriteReason(
  event: Pick<ExtractedEvent, 'rowKey' | 'venueId' | 'seriesId'>,
  fav: Pick<FavoritesContextValue, 'venueIds' | 'seriesIds' | 'eventIds'>
): 'event' | 'venue' | 'series' | null {
  if (fav.eventIds.has(event.rowKey)) return 'event';
  if (event.venueId && fav.venueIds.has(event.venueId)) return 'venue';
  if (event.seriesId && fav.seriesIds.has(event.seriesId)) return 'series';
  return null;
}
